import firebase from 'firebase/compat/app';
import 'firebase/compat/database';
import 'firebase/compat/auth';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_APIKEY,
  authDomain: process.env.REACT_APP_AUTHDOMAIN,
  databaseURL: process.env.REACT_APP_DATABASEURL,
  projectId: process.env.REACT_APP_PROJECTID,
  storageBucket: process.env.REACT_APP_STORAGEBUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGINGSENDERID,
  appId: process.env.REACT_APP_APPID,
  measurementId: process.env.REACT_APP_MEASUREMENTID,
};

function getRtdbApp() {
  return firebase.apps.length ? firebase.app() : firebase.initializeApp(firebaseConfig);
}

// This app authenticates admins against our own backend (JWT), never
// Firebase Auth directly — but RTDB security rules require auth != null even
// for reads, so the listener below was silently getting permission_denied on
// every attempt (confirmed via console: "Client doesn't have permission to
// access the desired data"). Mirrors the same anonymous-auth fix already
// applied to the rider app (Susi_Delivery/App.js) for the identical reason.
let anonAuthPromise = null;
function ensureAnonymousAuth(app) {
  if (!anonAuthPromise) {
    anonAuthPromise = app.auth().currentUser
      ? Promise.resolve()
      : app
          .auth()
          .signInAnonymously()
          .catch(err => {
            console.error('[firebaseRealtime] Anonymous sign-in failed:', err);
          });
  }
  return anonAuthPromise;
}

/**
 * Subscribes to order status changes mirrored into Realtime Database at
 * orders/{orderId}/status (written server-side by StatusBroadcastService on
 * every order creation/status transition). `onChange` is called with no
 * arguments on every add/change — this is purely an invalidation signal, not
 * a data source, since the RTDB payload here is only {status, updatedAt},
 * not a full order record. Callers should re-fetch from the scoped REST
 * endpoint in response.
 */
export function subscribeToOrderChanges(onChange) {
  const app = getRtdbApp();
  const ordersRef = app.database().ref('orders');
  const handler = () => onChange();
  // Surface RTDB errors instead of swallowing them. This listener reads the
  // WHOLE `orders` node (unlike the mobile app, which reads individual
  // `orders/{id}/status` paths), so a security rule that only grants
  // per-order read access would silently deny this and disable live updates
  // with no trace. If realtime refresh isn't working, this log is the first
  // thing to check — a PERMISSION_DENIED here means the RTDB rules need a
  // read grant on the `orders` collection for admin users.
  const onError = err => {
    console.error(
      '[firebaseRealtime] orders subscription error — live updates are ' +
        'disabled, falling back to manual refresh only:',
      err,
    );
  };
  let unsubscribed = false;
  ensureAnonymousAuth(app).then(() => {
    if (unsubscribed) return;
    ordersRef.on('child_added', handler, onError);
    ordersRef.on('child_changed', handler, onError);
  });
  return () => {
    unsubscribed = true;
    ordersRef.off('child_added', handler);
    ordersRef.off('child_changed', handler);
  };
}
